import { doc, getDoc } from 'firebase/firestore';

export const DEFAULT_USD_TO_KES_RATE = 130;

export const normalizeText = (value) => String(value || '').trim().toLowerCase();
export const normalizeCurrencyCode = (value) => String(value || '').trim().toUpperCase();

export const sanitizeUsdToKesRate = (value) => {
  const numericRate = Number(value);
  return Number.isFinite(numericRate) && numericRate > 0 ? numericRate : DEFAULT_USD_TO_KES_RATE;
};

export const fetchUsdToKesRate = async (db) => {
  try {
    const settingsSnap = await getDoc(doc(db, 'Settings', 'currency'));
    if (!settingsSnap.exists()) return DEFAULT_USD_TO_KES_RATE;
    return sanitizeUsdToKesRate(settingsSnap.data()?.usdToKesRate);
  } catch (error) {
    console.error('Failed to load USD/KES exchange rate:', error);
    return DEFAULT_USD_TO_KES_RATE;
  }
};

export const resolveStudentCurrency = (student = {}) => {
  const savedCurrency = normalizeCurrencyCode(student.currency || student.displayCurrency || student.paymentCurrency);
  const nationality = normalizeText(student.nationality || student.userNationality || student.UserNationality || student.country);

  if (nationality && nationality !== 'kenya') return 'USD';
  if (nationality === 'kenya') return 'KES';
  if (savedCurrency === 'USD') return 'USD';
  if (savedCurrency === 'KES') return 'KES';
  return 'KES';
};

export const getCurrencyConversionRate = (currency, usdToKesRate) => (
  normalizeCurrencyCode(currency) === 'USD' ? 1 / sanitizeUsdToKesRate(usdToKesRate) : 1
);

export const getDisplayAmountFromKes = (amountInKes, currency, usdToKesRate) => {
  const numericAmount = Number(amountInKes || 0);
  const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
  return normalizeCurrencyCode(currency) === 'USD'
    ? Number((safeAmount / sanitizeUsdToKesRate(usdToKesRate)).toFixed(2))
    : Math.round(safeAmount);
};

export const getPaystackMinorAmountFromKes = (amountInKes, usdToKesRate) => (
  Math.max(1, Math.round(getDisplayAmountFromKes(amountInKes, 'USD', usdToKesRate) * 100))
);

export const formatMoney = (amount, currency = 'KES') => {
  const safeCurrency = normalizeCurrencyCode(currency) === 'USD' ? 'USD' : 'KES';
  const numericAmount = Number(amount || 0);
  const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;

  return `${safeCurrency} ${safeAmount.toLocaleString(safeCurrency === 'USD' ? 'en-US' : 'en-KE', {
    minimumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
    maximumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
  })}`;
};

export const formatMoneyFromKes = (amountInKes, currency, usdToKesRate) => (
  formatMoney(getDisplayAmountFromKes(amountInKes, currency, usdToKesRate), currency)
);

export const resolvePaymentDisplay = (payment = {}, student = {}, usdToKesRate = DEFAULT_USD_TO_KES_RATE) => {
  const explicitDisplayCurrency = normalizeCurrencyCode(payment.displayCurrency || payment.paymentCurrency);
  const recordCurrency = normalizeCurrencyCode(payment.currency);
  const studentCurrency = resolveStudentCurrency(student);
  const displayCurrency = explicitDisplayCurrency || (recordCurrency === 'USD' && payment.displayAmount ? 'USD' : studentCurrency);

  if (displayCurrency === 'USD') {
    const savedDisplayAmount = Number(payment.displayAmount);
    if (Number.isFinite(savedDisplayAmount) && savedDisplayAmount > 0) {
      return { amount: savedDisplayAmount, currency: 'USD' };
    }

    if (recordCurrency === 'USD' && !payment.amountInKes && !payment.incomeAmount) {
      return { amount: Number(payment.amount || 0), currency: 'USD' };
    }

    return {
      amount: getDisplayAmountFromKes(payment.amountInKes || payment.incomeAmount || payment.amount, 'USD', usdToKesRate),
      currency: 'USD',
    };
  }

  return {
    amount: Number(payment.amountInKes || payment.incomeAmount || payment.amount || 0),
    currency: 'KES',
  };
};

export const resolveInvoiceDisplay = (invoice = {}, student = {}, usdToKesRate = DEFAULT_USD_TO_KES_RATE) => {
  const recordCurrency = normalizeCurrencyCode(invoice.displayCurrency || invoice.paymentCurrency || invoice.currency);
  const studentCurrency = resolveStudentCurrency(student);

  if (recordCurrency === 'USD') {
    const savedDisplayAmount = Number(invoice.displayAmount);
    if (Number.isFinite(savedDisplayAmount) && savedDisplayAmount > 0) {
      return { amount: savedDisplayAmount, currency: 'USD' };
    }

    if (invoice.amountInKes || invoice.incomeAmount) {
      return {
        amount: getDisplayAmountFromKes(invoice.amountInKes || invoice.incomeAmount, 'USD', usdToKesRate),
        currency: 'USD',
      };
    }

    return { amount: Number(invoice.amount || 0), currency: 'USD' };
  }

  if (studentCurrency === 'USD') {
    return {
      amount: getDisplayAmountFromKes(invoice.amountInKes || invoice.amount, 'USD', usdToKesRate),
      currency: 'USD',
    };
  }

  return {
    amount: Number(invoice.amountInKes || invoice.amount || 0),
    currency: 'KES',
  };
};
